import { Fragment, useMemo } from "react";
import { Pressable } from "react-native";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../../useAppTheme";
import Box from "../../Box";

type Props = {
	transcriptText?: string;
	selectedChoiceIds?: string[];
	correctAnswerIds?: string[];
	isAnswered?: boolean;
	inReviewMode?: boolean;
	disabled?: boolean;
	onToggleWord: (wordId: string) => void;
};

type TranscriptToken =
	| { type: "space"; value: string; key: string }
	| { type: "word"; value: string; id: string; key: string };

const tokenizeTranscript = (text: string): TranscriptToken[] => {
	let wordIndex = 0;
	let spaceIndex = 0;

	return text.split(/(\s+)/).map((token) => {
		if (!token || /^\s+$/.test(token)) {
			return {
				type: "space",
				value: token,
				key: `space-${spaceIndex++}`,
			} as const;
		}

		const wordId = `word-${wordIndex}`;
		wordIndex += 1;

		return {
			type: "word",
			value: token,
			id: wordId,
			key: wordId,
		} as const;
	});
};

const WordSelection = ({
	transcriptText,
	selectedChoiceIds = [],
	correctAnswerIds = [],
	isAnswered,
	inReviewMode,
	disabled = false,
	onToggleWord,
}: Props) => {
	const theme = useAppTheme();
	const tokens = useMemo(
		() => (transcriptText ? tokenizeTranscript(transcriptText) : []),
		[transcriptText],
	);
	const showReviewState = Boolean(isAnswered || inReviewMode);

	return (
		<Box>
			<Box flexDirection="row" flexWrap="wrap" alignItems="center">
				{tokens.map((token) => {
					if (token.type === "space") {
						return (
							<Text key={token.key} variant="bodyLarge">
								{token.value}
							</Text>
						);
					}

					const isSelected = selectedChoiceIds.includes(token.id);
					const isCorrect = correctAnswerIds.includes(token.id);

					let backgroundColor = "transparent";
					let textColor = theme.colors.onSurface;

					if (showReviewState) {
						if (isCorrect && isSelected) {
							backgroundColor = theme.palette.success.info;
							textColor = theme.colors.scrim;
						} else if (!isCorrect && isSelected) {
							backgroundColor = theme.palette.error.light;
							textColor = theme.colors.white;
						}
					} else if (isSelected) {
						backgroundColor = theme.palette.yellow.main;
						textColor = theme.colors.scrim;
					}

					return (
						<Fragment key={token.id}>
							<Pressable
								disabled={disabled}
								onPress={() => onToggleWord(token.id)}
								style={{
									borderRadius: theme.roundness,
									backgroundColor,
									paddingHorizontal: 2,
								}}
							>
								<Text
									variant="bodyLarge"
									style={{
										fontSize: 16,
										lineHeight: 24,
										color: textColor,
									}}
								>
									{token.value}
								</Text>
							</Pressable>
						</Fragment>
					);
				})}
			</Box>
		</Box>
	);
};

export default WordSelection;
